/**
 * Structured data the backend extracts from an uploaded onboarding document
 * (PDF or image). The backend owns the prompt and output schema and returns
 * this already-normalised shape; the frontend only consumes it.
 */
export interface ExtractedTeam {
  name: string;
}

export interface ExtractedShiftTemplate {
  name: string;
  startTime: string;
  endTime: string;
}

export interface ExtractedEmployee {
  firstName: string;
  lastName: string;
  email?: string;
  team?: string;
}

/** One recurring-shift row of the recommended weekly schedule (lenient plain strings). */
export interface ScheduleRecommendationEntry {
  teamName: string;
  shiftTemplateName: string;
  days: string[];
  employeeName?: string;
}

/** Recommended recurring schedule the model infers, kept lenient (plain strings). */
export type ScheduleRecommendation = ScheduleRecommendationEntry[];

export interface ExtractedData {
  teams: ExtractedTeam[];
  shiftTemplates: ExtractedShiftTemplate[];
  employees: ExtractedEmployee[];
  /**
   * If the account owner appears in the schedule, the team they are scheduled
   * in; empty string otherwise. The owner is recognised server-side from the
   * session, so the frontend no longer sends their name.
   */
  currentUserTeam: string;
  /**
   * Recommended recurring weekly schedule inferred from the document, reconciled
   * later against the entities actually created during onboarding. Empty array
   * when the document has no discernible schedule.
   */
  scheduleRecommendation: ScheduleRecommendation;
}

/** File formats accepted for extraction (natively supported by the Messages API). */
export const ACCEPTED_UPLOAD_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const;

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB — matches the server-side cap.

// Cap on the optional free-text note the user can add to describe the file.
// Keeps the extra prompt context to a handful of tokens.
export const MAX_CONTEXT_LENGTH = 500;

export type ExtractionStatus = 'resolved' | 'empty' | 'error';

export interface ExtractionResult {
  status: ExtractionStatus;
  data: ExtractedData;
}

/** What the review step hands off, after the user has selected/edited the findings. */
export interface ImportSelection {
  teams: { name: string }[];
  shiftTemplates: { name: string; startTime: string; endTime: string }[];
  employees: { firstName: string; lastName: string; email?: string; teamName?: string }[];
  /** Team the account owner is scheduled in (they already have a row — this just sets its team). */
  currentUserTeam?: string;
}

/**
 * Google Places-derived address for the selected business, stashed during the
 * AI-assisted signup so the default department can be updated with it once the
 * account exists (the CRM create call doesn't accept address fields).
 */
export interface ImportPlaceAddress {
  address?: string;
  latitude?: number;
  longitude?: number;
}
